import React, { useEffect, useRef, useState } from "react";
import * as yup from "yup";
import { useFormik } from "formik";
import BaseModal from "../../../components/ui/BaseModal";
import BaseButton from "../../../components/ui/BaseButton";
import BaseAutoSelect from "../../../components/ui/BaseAutoSelect";
import { useMutation, useQuery } from "@tanstack/react-query";
import { fetchCategories, fetchSubCategories, fetchVendors } from "../../../services/api/category";
import { addSkillsToUser, deleteUserSkill } from "../../../services/api/user";
import { useDispatch } from "react-redux";
import { showToast } from "../../../store/toast/toastSlice";
import { useParams } from "react-router-dom";

const validationSchema = yup.object({
  categoryId: yup.string("").required("required"),
  vendorId: yup.string("").required("required"),
  subCategoryIds: yup
    .array()
    .of(yup.string().required("Sub-category is required"))
    .min(1, "At least one sub-category is required")
    .required("Sub-category selection is required"),
});

const AddSkillsModal = ({ open, close, refetchUserSkills, skills, userId }) => {
  const dispatch = useDispatch();
  const { id } = useParams();
  const formik = useFormik({
    initialValues: {
      categoryId: null,
      vendorId: null,
      subCategoryIds: [],
    },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      handleAddUserSkills();
    },
    validateOnChange: true,
    validateOnBlur: true,
  });
  const {
    isLoading: IsCategoriesLoading,
    error: isCategoriesError,
    data: categoriesData,
  } = useQuery({
    queryKey: ["categories"],
    queryFn: fetchCategories,
  });

  const {
    isLoading: IsVendorsLoading,
    error: isVendorsError,
    data: vendorsData,
  } = useQuery({
    queryKey: ["vendorsData", formik.values.categoryId], // ✅ Use formik.values.categoryId directly
    queryFn: () => fetchVendors(formik.values.categoryId),
    enabled: !!formik.values.categoryId,
  });

  const {
    isLoading: IsSubCategoriesLoading,
    error: isSubCategoriesError,
    data: subCategoriesData,
  } = useQuery({
    queryKey: ["subCategories", formik?.values.vendorId], // Add vendorId to the queryKey
    queryFn: () => fetchSubCategories(formik?.values.vendorId),
    enabled: !!formik?.values.vendorId,
  });

  const {
    isPending: isAddSkillsLoading,
    error: isAddSkillsError,
    data: addedSkillsData,
    mutateAsync: addSkillsToUserMutation,
  } = useMutation({
    mutationFn: addSkillsToUser,
    enabled: false,
  });

  const {
    isPending: isSkillDeletedApiLoading,
    error: isSkillDeletedApiError,
    data: deletedSkillData,
    mutateAsync: deleteUserSkillMutation,
  } = useMutation({
    mutationFn: (skillId) => deleteUserSkill(skillId, userId),
    enabled: false,
  });

  useEffect(() => {
    formik.setFieldValue("vendorId", null);
    formik.setFieldValue("subCategoryIds", []);
    formik.setFieldTouched("vendorId", false);
    formik.setFieldTouched("subCategoryIds", false);
  }, [formik.values.categoryId]);

  useEffect(() => {
    formik.setFieldValue("subCategoryIds", []);
    formik.setFieldTouched("subCategoryIds", false);
  }, [formik.values.vendorId]);

  const handleAddUserSkills = async () => {
    try {
      const userAddedSkillsResponse = await addSkillsToUserMutation({
        subCategoryIds: formik.values.subCategoryIds,
        id: id || userId,
      });
      dispatch(
        showToast({
          message: "Skills added successfully",
          type: "success",
        }),
      );
      refetchUserSkills();
      formik.resetForm();
      close(false);
    } catch (error) {
      const response = error?.response?.data;

      let errorMessage = "Something went wrong";

      if (typeof response?.message === "string") {
        errorMessage = response.message;
      }

      if (Array.isArray(response?.existingSkills)) {
        errorMessage = `These skills already exist: ${response.existingSkills.join(", ")}`;
      }

      dispatch(
        showToast({
          message: errorMessage,
          type: "error",
        }),
      );
    }
  };

  const handleDeleteUserSkill = async (id) => {
    try {
      await deleteUserSkillMutation(id);
      refetchUserSkills();
    } catch (error) {
      dispatch(
        showToast({
          message: error.response.data.message,
          type: "error",
        }),
      );
    }
  };

  const formattedCategories = categoriesData?.data?.map((category) => ({
    label: category.category,
    value: category.id,
  }));

  const formattedVendors = vendorsData?.data?.map((vendor) => ({
    label: vendor.name,
    value: vendor.id,
  }));

  const formattedSubCategories = subCategoriesData?.data
    ?.filter((subCategory) => {
      return !skills?.some((skill) => skill.subCategory === subCategory.subCategory);
    })
    ?.map((subCategory) => ({
      label: subCategory.subCategory,
      value: subCategory.id,
    }));

  return (
    <form>
      <BaseModal className="-top-[200px] overflow-auto" open={open} close={close}>
        <div className="py-7 px-5 sm:px-12 w-full lg:w-[900px] xl:w-[1000px]">
          <h6 className="text-dark font-normal border-b pb-1">Add Skills</h6>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-8 mt-4 [&>*]:min-w-0">
            <BaseAutoSelect
              className="mb-3"
              placeholder="Select Technology Stack"
              fullHeight={false}
              required={true}
              labelShrink={false}
              label="Technology Stacks"
              options={formattedCategories}
              id="categoryId"
              name="categoryId"
              isDefaultField={false}
              defaultValue={formik.values.categoryId}
              value={formik.values.categoryId}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.categoryId && Boolean(formik.errors.categoryId)}
              helperText={formik.touched.categoryId && formik.errors.categoryId}
            />
            <BaseAutoSelect
              className="mb-3"
              placeholder="Select Category"
              fullHeight={false}
              required={true}
              labelShrink={false}
              label="Category"
              options={formattedVendors}
              isDefaultField={false}
              id="vendorId"
              name="vendorId"
              defaultValue={formik.values.vendorId}
              value={formik.values.vendorId}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.vendorId && Boolean(formik.errors.vendorId)}
              helperText={formik.touched.vendorId && formik.errors.vendorId}
            />
            <BaseAutoSelect
              className="mb-3"
              placeholder="Select Skills"
              fullHeight={false}
              required={true}
              labelShrink={false}
              label="Skills"
              isDefaultField={false}
              options={formattedSubCategories}
              multiple
              disableCloseOnSelect
              id="subCategoryIds"
              name="subCategoryIds"
              defaultValue={formik.values.subCategoryIds}
              value={formik.values.subCategoryIds}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.subCategoryIds && Boolean(formik.errors.subCategoryIds)}
              helperText={formik.touched.subCategoryIds && formik.errors.subCategoryIds}
            />
          </div>
          <div className="flex flex-wrap gap-4">
            {skills?.map((skill) => (
              <div className="flex items-center px-4 py-1 bg-light rounded-sm">
                <p key={skill.id}>{skill?.subCategory}</p>
                <i
                  className="fa-solid fa-xmark ml-2 cursor-pointer hover:text-blue-500"
                  onClick={() => handleDeleteUserSkill(skill.id)}
                ></i>
              </div>
            ))}
          </div>
        </div>
        <div
          className="d-flex content-center bg-white py-6 mt-6"
          style={{
            boxShadow: "0px 0px 14px 0px rgba(0, 0, 0, 0.07)",
          }}
        >
          <BaseButton
            className="px-[28px] py-[5px] bg-light text-primary !hover:text-primary"
            variant="outlined"
            color="primary"
            style={{
              marginRight: "16px",
            }}
            onClick={() => close(false)}
          >
            Cancel
          </BaseButton>

          <BaseButton
            loaderSize={24}
            loaderColor="primary"
            className="px-[12px] py-[5px]"
            color="primary"
            onClick={formik.handleSubmit}
            // Disabled by default; enabled only when there are valid unsaved changes.
            disabled={!formik.dirty || !formik.isValid}
          >
            Save Changes
          </BaseButton>
        </div>
      </BaseModal>
    </form>
  );
};

export default AddSkillsModal;
